import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, Crown, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const [state, setState] = useState("checking"); // checking | paid | expired | timeout

    useEffect(() => {
        if (!sessionId) { setState("timeout"); return; }
        let attempts = 0, cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") { await refreshUser(); setState("paid"); return; }
                if (data.status === "expired" || data.payment_status === "failed") { setState("expired"); return; }
            } catch (_) {}
            if (++attempts >= 8) { setState("timeout"); return; }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-background">
            <Navbar />
            <main className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="payment-success-page">
                {state === "checking" && (
                    <>
                        <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
                        <h1 className="mt-6 font-heading text-2xl font-extrabold text-foreground">Confirming your payment…</h1>
                        <p className="mt-2 text-sm text-muted-foreground">Hang tight, this only takes a moment.</p>
                    </>
                )}
                {state === "paid" && (
                    <>
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/15 border border-accent/30">
                            <Crown className="h-8 w-8 text-accent" />
                        </div>
                        <h1 className="mt-6 font-heading text-3xl font-extrabold text-foreground" data-testid="payment-success-title">You're on Sift Pro</h1>
                        <p className="mt-2 text-sm text-muted-foreground">Savings Finder, subscription tracker, month comparison and unlimited Ask Sift are now unlocked.</p>
                        <Button onClick={() => navigate("/dashboard")} data-testid="payment-success-dashboard-btn" className="mt-8 bg-primary text-primary-foreground hover:bg-primary/90">
                            <CheckCircle2 className="mr-2 h-4 w-4" /> Back to my money
                        </Button>
                    </>
                )}
                {(state === "expired" || state === "timeout") && (
                    <>
                        <XCircle className="mx-auto h-12 w-12 text-destructive" />
                        <h1 className="mt-6 font-heading text-2xl font-extrabold text-foreground">
                            {state === "expired" ? "Payment didn't complete" : "Still waiting on Stripe"}
                        </h1>
                        <p className="mt-2 text-sm text-muted-foreground">
                            {state === "expired" ? "The checkout session expired or failed. You haven't been charged." : "Your payment may still be processing. Check back in a minute — you won't be charged twice."}
                        </p>
                        <Link to="/dashboard" className="mt-8 inline-block text-sm text-accent hover:underline" data-testid="payment-back-link">Back to dashboard</Link>
                    </>
                )}
            </main>
        </div>
    );
}
